import { ChangeDetectionStrategy, Component, inject, signal, computed, effect } from '@angular/core';
import { LottieComponent } from 'ngx-lottie';
import { toSignal } from '@angular/core/rxjs-interop';
import { first } from 'rxjs';

import { LottieService } from '../../../services/lottie.service';
import { TutorialStorageService } from '../../../services/tutorial-storage.service';

/** Tutorial overlay message component. */
@Component({
	selector: 'gaw-tutorial-overlay',
	templateUrl: './tutorial-overlay.component.html',
	styleUrls: ['./tutorial-overlay.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [LottieComponent],
})
export class TutorialOverlayComponent {
	private readonly lottieService = inject(LottieService);

	private readonly tutorialStorage = inject(TutorialStorageService);

	/** Whether animation is visible. */
	protected readonly isVisible = signal(true);

	/** Animations. */
	protected readonly animations = toSignal(this.lottieService.animations$);

	private readonly isShown = this.tutorialStorage.isShown;

	/** Current animation index. */
	protected readonly currentAnimationIndex = signal(0);

	/** Animations count. */
	protected readonly animationsCount = computed(() => this.animations()?.length ?? 0);

	/** Current animation. */
	protected readonly currentAnimation = computed(() => {
		const animations = this.animations();
		const index = this.currentAnimationIndex();
		return animations?.[index] ?? null;
	});

	/** Whether the last slide is shown or not. */
	protected readonly isLastSlide = computed(() => this.currentAnimationIndex() === this.animationsCount() - 1);

	/** @inheritdoc */
	public constructor() {
		effect(() => {
			this.isVisible.set(!this.isShown());
			if (!this.isShown()) {
				this.currentAnimationIndex.set(0);
			}
		});
	}

	/**
	 * Switch animation.
	 * @param offset Offset.
	 */
	protected switchAnimation(offset: -1 | 1): void {
		const animations = this.animations();
		const currentIndex = this.currentAnimationIndex() + offset;
		if (!animations) {
			return;
		}

		if (currentIndex >= 0 && currentIndex < animations.length) {
			this.currentAnimationIndex.set(currentIndex);
		}
	}

	/** Close tutorial. */
	protected closeTutorial(): void {
		this.tutorialStorage.saveIsShown(true).pipe(first())
			.subscribe();
	}
}
